import * as Sentry from '@sentry/react';
import { Map } from 'immutable';

import { getIntervalsFromConfig, getPollingInterval, getTopShopId, isInvalid, shapeShopsForApp, updateState } from '@/utils/shops';
import { getActualDate, setDay } from '@/utils/time';
import { createController } from '@/utils/controller';
import { createPolling } from '@/utils/polling';
import { shapeNumber } from '@/utils/listings/listings';
import { navigation } from '@/utils/navigation';
import { getSize } from '@/utils/iterable/getSize';
import { get } from '@/utils/iterable/get';
import { api } from '@/utils/api';

import { POLLING_INTERVAL, STATUSES } from '@/constants/shops';
import { CHANNEL_ID, CHANNEL_NAME } from '@/constants/channels';
import { MESSAGE, NOTIFICATION } from '@/constants/notifications';
import { PAGES, PARAMS } from '@/constants/routes';
import { INITIAL_STATE } from '@/constants/store';
import { EMPTY, VELA } from '@/constants';
import { FEATURE } from '@/constants/features';
import { STATUS } from '@/constants/listings';
import { MODALS } from '@/constants/modal';

import actions from '@/actions';

const polling = createPolling();
const pollingController = createController();
const creditsController = createController();

function abort(controller) {
  if (controller.isActive()) {
    controller.stop();
  }
}

function* createVelaOnlyShop(reduction, { shopId, channel, db, optimize }) {
  yield function effect(dispatch) {
    api.shops.createVelaShop({ channel, db, shopId, optimize })
      .then(
        (response) => dispatch(actions.shops.createVelaOnlyShop.fulfilled({ response, channel, shopId })),
        (error) => dispatch(actions.shops.createVelaOnlyShop.rejected(error)),
      );
  };

  return reduction;
}

function* createVelaOnlyShopFulfilled(reduction, { response, channel }) {
  if (response?.result === 'success') {
    const { target_db: db, target_shop: shopId } = response;

    yield function effect(dispatch) {
      dispatch(actions.shops.setData({
        path: ['byId', shopId],
        value: Map({
          channel,
          channelId: CHANNEL_ID[channel],
          db,
          id: shopId,
          isVelaOnlyShop: true,
          name: `New ${CHANNEL_NAME[channel]}`,
          syncStatus: STATUSES.UP_TO_DATE,
          marketplaceId: 'EBAY_US',
        }),
      }));

      navigation().goTo({
        page: PAGES.LISTINGS,
        params: { [PARAMS.SHOP_ID]: shopId, [PARAMS.STATUS]: STATUS[VELA].COPIED },
      });
    };
  }

  return reduction;
}

function* createVelaOnlyShopRejected(reduction, error) {
  yield function effect() {
    console.error(error);
  };

  return reduction;
}

function* getActiveListingsCount(reduction) {
  yield function effect(dispatch) {
    api.shops.getActiveListingsCount()
      .then(
        (response) => dispatch(actions.shops.getActiveListingsCount.fulfilled(response)),
        (error) => dispatch(actions.shops.getActiveListingsCount.rejected(error)),
      );
  };

  return reduction;
}

function* getActiveListingsCountFulfilled(reduction, response) {
  return reduction.setIn(['shops', 'activeListingsCount'], Map(response));
}

function* getActiveListingsCountRejected(reduction, error) {
  yield function effect() {
    console.error(error);
  };

  return reduction;
}

function* getShopCredits(state, { shopId, update }) {
  const { signal } = creditsController.start();

  yield function effect(dispatch) {
    const db = state.getIn(['shops', 'byId', shopId, 'db']);

    api.billing.getShopCredits({ db, shopId, signal })
      .then((response) => dispatch(actions.shops.getShopCredits.fulfilled({ response, shopId })))
      .catch((error) => dispatch(actions.shops.getShopCredits.rejected({ error, shopId, signal })));
  };

  return typeof update === 'function'
    ? state.updateIn(['shops', 'credits', shopId], update)
    : state;
}

function* getShopCreditsFulfilled(state, { response, shopId }) {
  abort(creditsController);
  const { purchased_credits: purchased, subscription_credits: subscription } = response;
  return state.setIn(['shops', 'credits', shopId], shapeNumber(purchased) + shapeNumber(subscription));
}

function* getShopCreditsRejected(state, { error, signal }) {
  if (signal.aborted) return;

  abort(creditsController);

  if (error) {
    console.error(error);
  }

  return state;
}

function* getShops(reduction, nextShopId) {
  let state = reduction;
  let previousShopId;
  const { signal } = pollingController.start();
  const interval = state.getIn(['shops', 'pollingInterval']);

  if (
    nextShopId && (
      !state.hasIn(['shops', 'current']) ||
      state.hasIn(['shops', 'byId', nextShopId])
    )
  ) {
    previousShopId = state.setIn(['shops', 'current']);
    state = state.setIn(['shops', 'current'], nextShopId);
  }

  yield function effect(dispatch) {
    function getStatus() {
      dispatch(actions.shops.getShops());
    }

    if (interval) {
      polling.start(setTimeout(getStatus, interval));
    }

    api
      .get({ url: '/shops/status', signal })
      .then(
        (response) => dispatch(actions.shops.getShops.fulfilled({ previousShopId, response })),
        (error) => dispatch(actions.shops.getShops.rejected({ error, signal }))
      );
  };

  return state;
}

function* getShopsFulfilled(reduction, { previousShopId, response }) {
  abort(pollingController);

  let state = reduction;

  if (state.hasIn(['user', 'userId'])) {
    if (state.getIn(['user', 'userId']) !== response.userId) {
      // got shops of another user, need to reload app
      yield function effect(dispatch) {
        dispatch(actions.user.getUserData());
      };

      return INITIAL_STATE.setIn(['auth', 'loggedIn'], state.getIn(['auth', 'loggedIn']));
    }
  } else {
    state = state.setIn(['user', 'userId'], response.userId);
  }

  let current = state.getIn(['shops', 'current']);
  const intervals = getIntervalsFromConfig(state);
  const userFeatures = reduction.getIn(['user', 'features'], EMPTY.MAP);
  const { byId, options } = shapeShopsForApp({ response, userFeatures });

  if (!getSize(byId)) {
    return state
      .setIn(['shops', 'byId'], EMPTY.MAP)
      .setIn(['shops', 'options'], EMPTY.LIST);
  }

  if (
    (current && !byId.has(current)) ||
    (state.hasIn(['shops', 'options']) && !state.getIn(['shops', 'options']).equals(options))
  ) {
    const interval = intervals.short;

    yield function effect(dispatch) {
      // start_trial conversion: fire only when backend signals new shop or vela-only→channel (new_shop=1 on redirect).
      // Root handles that via search.new_shop === '1' and pushStartTrial(); do not fire here on "list grew" (could be reconnection).
      dispatch(actions.user.getSubscriptions());

      function getStatus() {
        dispatch(actions.shops.getShops());
      }

      if (getSize(state.get('billing'))) {
        const shopInManage = state.getIn(['billing', 'manage', 'shopId']);

        if (shopInManage && !byId.has(shopInManage)) {
          dispatch(actions.billing.manageShop());
        }
      }
      polling.start(setTimeout(getStatus, interval));
    };

    if (!byId.has(current)) {
      current = options.get(0);
    }

    return state
      .setIn(['shops', 'byId'], byId)
      .setIn(['shops', 'current'], current)
      .setIn(['shops', 'options'], options)
      .setIn(['shops', 'pollingInterval'], interval);

  } else if (!current) {
    current = getTopShopId({ shops: Map({ byId, options }), user: state.get('user') });
  }

  const currentShop = byId.get(current);
  const currentSyncStatus = currentShop.get('syncStatus');

  if (previousShopId) {
    yield function effect(dispatch) {
      if (!state.hasIn(['shops', 'schedule', current])) {
        dispatch(actions.shops.getScheduled(current));
      }

      Sentry.setTag('shop_id', current);
      Sentry.setTag('shop_db', currentShop.get('db'));
    };
  }

  if (
    state.getIn(['user', 'features', FEATURE.AI_CREDITS]) &&
    !state.hasIn(['shops', 'credits', current]) &&
    !currentShop.get('isVelaOnlyShop')
  ) {
    yield function effect(dispatch) {
      dispatch(actions.shops.getShopCredits({ shopId: current }));
    };
  }

  if (
    isInvalid(currentSyncStatus) &&
    !isInvalid(state.getIn(['shops', 'byId', current, 'syncStatus']))
  ) {
    polling.stop();

    state = state
      .setIn(['shops', 'byId'], byId)
      .setIn(['shops', 'current'], current)
      .setIn(['shops', 'options'], options)
      .deleteIn(['shops', 'syncData', current])
      .deleteIn(['shops', STATUSES.REFRESH_USER, current]);

    state = state
      .deleteIn(['listings', 'placeholders', current])
      .deleteIn(['listings', 'products', 'processing', current]);

    return state;
  }

  const { actions: actionsToDispatch, state: newState } = options.reduce(
    updateState({ current, shopsById: byId }),
    { actions: [], state },
  );

  state = newState;

  if (getSize(actionsToDispatch)) {
    yield function effect(dispatch) {
      actionsToDispatch.forEach(dispatch);
    };
  }

  if (state.getIn(['shops', 'current']) !== current) {
    state = state.setIn(['shops', 'current'], current);
  }

  if (!byId.equals(state.getIn(['shops', 'byId']))) {
    state = state.setIn(['shops', 'byId'], byId);
  }

  if (!options.equals(state.getIn(['shops', 'options']))) {
    state = state.setIn(['shops', 'options'], options);
  }

  const newInterval = getPollingInterval({ currentShopId: current, intervals, state });

  if (newInterval !== state.getIn(['shops', 'pollingInterval'])) {
    state = state.setIn(['shops', 'pollingInterval'], newInterval);

    yield function effect(dispatch) {
      function getStatus() {
        dispatch(actions.shops.getShops());
      }

      polling.start(setTimeout(getStatus, newInterval));
    };
  }

  return state;
}

function* getShopsRejected(reduction, { error, signal }) {
  if (signal.aborted) return reduction;

  abort(pollingController);
  console.error(error);

  yield function effect(dispatch) {
    dispatch(actions.shops.rescheduleShopsPolling());
  };

  return reduction;
}

function* getScheduled(state, shopId) {
  const db = state.getIn(['shops', 'byId', shopId, 'db']);

  if (!shopId || !db) return state;

  yield function effect(dispatch) {
    const actualDate = getActualDate();

    const since = actualDate.toISOString();
    const to = setDay(actualDate, actualDate.getDate() + 1).toISOString();

    const requests = [
      api.schedule.updates.get({ db, params: { type: 'updates', since, to }, shopId }),
      api.schedule.updates.get({ db, params: { type: 'publish', since, to }, shopId }),
    ];

    Promise
      .all(requests)
      .then(
        (responses) => dispatch(actions.shops.getScheduled.fulfilled({ responses, shopId })),
        (error) => dispatch(actions.shops.getScheduled.rejected({ error, shopId })),
      );
  };

  return state.setIn(['shops', 'schedule', shopId, 'loading'], true);
}

function* getScheduledFulfilled(state, { responses, shopId }) {
  let schedule = state.getIn(['shops', 'schedule'], EMPTY.MAP).deleteIn([shopId, 'loading']);
  const [updates, listings] = responses;
  const nextUpdate = get(['scheduled_updates', 0])(updates);
  const nextPublish = get(['scheduled_updates', 0])(listings);

  if (getSize(nextUpdate) > 0) {
    schedule = schedule.setIn([shopId, 'nextUpdate'], Map(nextUpdate));
  } else {
    schedule = schedule.deleteIn([shopId, 'nextUpdate']);
  }

  if (getSize(nextPublish) > 0) {
    schedule = schedule.setIn([shopId, 'nextPublish'], Map(nextPublish));
  } else {
    schedule = schedule.deleteIn([shopId, 'nextPublish']);
  }

  if (getSize(schedule.get(shopId)) === 0) {
    schedule = schedule.delete(shopId);
  }

  return state.setIn(['shops', 'schedule'], schedule);
}

function* getScheduledRejected(state, { error, shopId }) {
  yield function effect() {
    console.error(error);
  };

  let schedule = state.getIn(['shops', 'schedule'], EMPTY.MAP).deleteIn([shopId, 'loading']);

  if (getSize(schedule.get(shopId)) === 0) {
    schedule = schedule.delete(shopId);
  }

  return state.setIn(['shops', 'schedule'], schedule);
}

function* refreshUserInitiatedShop(reduction, shopId) {
  if (!reduction.hasIn(['shops', 'byId', shopId])) {
    console.error(`Error: Cannot refresh shop, no shop data for shopId "${shopId}"`);
    return reduction;
  }

  const shop = reduction.getIn(['shops', 'byId', shopId]);
  const channel = shop.get('channel');
  const db = shop.get('db');

  yield function effect(dispatch) {
    dispatch(
      actions.shops.setData({
        path: ['syncData', shopId],
        value: Map({ modal: MODALS.SYNC.REFRESH }),
      })
    );

    api.shops
      .refresh({ channel, db, shopId })
      .then(
        () => dispatch(actions.shops.getShops()),
        (error) => dispatch(actions.shops.refreshUserInitiatedShop.rejected({ error, shopId })),
      );
  };

  return reduction;
}

function* refreshUserInitiatedShopRejected(reduction, { error, shopId }) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(actions.shops.setData({ path: ['syncData', shopId] }));
    dispatch(
      actions.notifications.add({
        message: MESSAGE.FAIL.REFRESH_SHOP,
        type: NOTIFICATION.ERROR,
      })
    );
  };

  return reduction;
}

function* refreshUserShop(reduction, shopId) {
  yield function effect() {
    api.get({
      url: `/shops/${shopId}/refresh`,
      params: {
        userAction: STATUSES.REFRESH_USER,
        db: reduction.getIn(['shops', 'byId', shopId, 'db']),
      },
    });
  };

  return reduction.setIn(['shops', 'refreshing', shopId], true);
}

function* rescheduleShopsPolling(reduction, interval) {
  polling.stop();

  let state = reduction;
  const newInterval = (
    interval ||
    state.getIn(['shops', 'pollingInterval']) ||
    state.getIn(['user', 'config', 'shopsPollingIntervalLong'], POLLING_INTERVAL.LONG)
  );

  if (newInterval !== state.getIn(['shops', 'pollingInterval'])) {
    state = state.setIn(['shops', 'pollingInterval'], newInterval);
  }

  yield function effect(dispatch) {
    function getStatus() {
      dispatch(actions.shops.getShops());
    }

    polling.start(setTimeout(getStatus, newInterval));
  };

  return state;
}

function* setData(reduction, { path, value }) {
  const fullPath = Array.isArray(path)
    ? ['shops', ...path]
    : ['shops', path];

  if (value) return reduction.setIn(fullPath, value);

  let state = reduction.deleteIn(fullPath);

  fullPath.pop();

  while (fullPath.length > 1) {
    if (!getSize(state.getIn(fullPath))) {
      state = state.deleteIn(fullPath);
    }

    fullPath.pop();
  }

  return state;
}

function* showSyncModal(reduction, { shopId, type }) {
  yield function effect(dispatch) {
    dispatch(
      actions.shops.setData({
        path: ['syncData', shopId],
        value: Map({ modal: type }),
      })
    );

    dispatch(
      actions.shops.rescheduleShopsPolling(
        reduction.getIn(['user', 'config', 'shopsPollingIntervalShort']) ||
        POLLING_INTERVAL.SHORT
      )
    );
  };

  return reduction;
}

function* validateOauthToken(reduction, token) {
  yield function effect(dispatch) {
    api
      .get({ url: '/getOauthFailedToken', params: { token }})
      .then(() => dispatch(actions.shops.setData({ path: 'importFailed', value: true })));
  };

  return reduction;
}

function* resetPublicError(reduction, shopId) {
  const db = reduction.getIn(['shops', 'byId', shopId, 'db']);

  yield function effect(dispatch) {
    api.shops.resetPublicError({ shopId, db })
      .then(() => dispatch(actions.shops.getShops()));
  };

  return reduction;
}

const shops = {
  [actions.shops.createVelaOnlyShop]: createVelaOnlyShop,
  [actions.shops.createVelaOnlyShop.fulfilled]: createVelaOnlyShopFulfilled,
  [actions.shops.createVelaOnlyShop.rejected]: createVelaOnlyShopRejected,
  [actions.shops.getActiveListingsCount]: getActiveListingsCount,
  [actions.shops.getActiveListingsCount.fulfilled]: getActiveListingsCountFulfilled,
  [actions.shops.getActiveListingsCount.rejected]: getActiveListingsCountRejected,
  [actions.shops.getShopCredits]: getShopCredits,
  [actions.shops.getShopCredits.fulfilled]: getShopCreditsFulfilled,
  [actions.shops.getShopCredits.rejected]: getShopCreditsRejected,
  [actions.shops.getShops]: getShops,
  [actions.shops.getShops.fulfilled]: getShopsFulfilled,
  [actions.shops.getShops.rejected]: getShopsRejected,
  [actions.shops.getScheduled]: getScheduled,
  [actions.shops.getScheduled.fulfilled]: getScheduledFulfilled,
  [actions.shops.getScheduled.rejected]: getScheduledRejected,
  [actions.shops.refreshUserInitiatedShop]: refreshUserInitiatedShop,
  [actions.shops.refreshUserInitiatedShop.rejected]: refreshUserInitiatedShopRejected,
  [actions.shops.resetPublicError]: resetPublicError,
  [actions.shops.refreshUserShop]: refreshUserShop,
  [actions.shops.rescheduleShopsPolling]: rescheduleShopsPolling,
  [actions.shops.setData]: setData,
  [actions.shops.showSyncModal]: showSyncModal,
  [actions.shops.validateOauthToken]: validateOauthToken,
};

export default shops;
